import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ArrowLeft, Phone, Mail, MapPin, Clock,
    MessageCircle, ArrowRight, CheckCircle,
} from 'lucide-react';

/* ── WhatsApp number & helpers ───────────────────────────────────── */
const WA_NUMBER = '971581735200';

const buildWaLink = (name, service, message) => {
    const text = [
        `Hello Royal Standard! 👋`,
        name ? `My name is ${name}.` : '',
        service ? `I'm interested in: *${service}*.` : '',
        message ? `\n${message}` : '',
        '\nLooking forward to hearing from you.',
    ].filter(Boolean).join('\n');
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;
};

const SERVICES = [
    'Marble Polishing',
    'Tile & Marble Installation',
    'Ceiling Works',
    'Joinery & Carpentry',
    'Other / General Enquiry',
];

/* ── Floating WhatsApp pill (bottom-right) ───────────────────────── */
export const WhatsAppFAB = () => (
    <motion.a
        href={`https://wa.me/${WA_NUMBER}`}
        target="_blank"
        rel="noreferrer"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.5, type: 'spring', stiffness: 200 }}
        whileHover={{ scale: 1.08 }}
        className="fixed bottom-6 right-6 z-[9999] flex items-center gap-2 px-5 py-3 rounded-full text-white text-sm font-semibold shadow-2xl"
        style={{ background: '#25D366', boxShadow: '0 8px 32px rgba(37,211,102,0.45)' }}
    >
        <MessageCircle size={20} />
        Chat on WhatsApp
    </motion.a>
);

/* ── Main Contact Page ───────────────────────────────────────────── */
const ContactPage = () => {
    const [name, setName] = useState('');
    const [service, setService] = useState('');
    const [message, setMessage] = useState('');

    return (
        <div className="font-sans bg-[#0e0e0e] min-h-screen text-white">

            {/* ── Sticky nav bar ── */}
            <div
                className="fixed top-0 left-0 w-full z-50"
                style={{ background: 'rgba(14,14,14,0.88)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(212,175,55,0.12)' }}
            >
                {/* Desktop: single space-between row */}
                <div className="hidden md:flex items-center justify-between px-8 py-4">
                    <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-xs uppercase tracking-widest">
                        <ArrowLeft size={15} /> Home
                    </Link>
                    <Link to="/" className="flex items-center gap-2 leading-none">
                        <span className="font-serif text-base text-white tracking-widest">ROYAL</span>
                        <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '28px', width: 'auto', objectFit: 'contain', display: 'block' }} />
                        <span className="font-serif text-base tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                    </Link>
                    <a
                        href={`https://wa.me/${WA_NUMBER}`}
                        target="_blank" rel="noreferrer"
                        className="flex items-center gap-2 text-xs uppercase tracking-widest px-5 py-2.5 font-semibold rounded-full text-white"
                        style={{ background: '#25D366' }}
                    >
                        <MessageCircle size={14} /> WhatsApp
                    </a>
                </div>

                {/* Mobile: compact logo row + full-width WhatsApp strip */}
                <div className="md:hidden">
                    <div className="flex items-center justify-between px-4 py-3">
                        <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors text-xs uppercase tracking-wider">
                            <ArrowLeft size={14} />
                            <span className="hidden xs:inline">Home</span>
                        </Link>
                        <Link to="/" className="flex items-center gap-1.5 leading-none">
                            <span className="font-serif text-sm text-white tracking-widest">ROYAL</span>
                            <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '22px', width: 'auto', objectFit: 'contain', display: 'block' }} />
                            <span className="font-serif text-sm tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                        </Link>
                        <div className="w-8" />
                    </div>
                    <a
                        href={`https://wa.me/${WA_NUMBER}`}
                        target="_blank" rel="noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-2.5 text-white text-xs uppercase tracking-widest font-semibold"
                        style={{ background: '#25D366' }}
                    >
                        <MessageCircle size={13} /> Chat on WhatsApp
                    </a>
                </div>
            </div>

            {/* ── Hero ── */}
            <section className="relative min-h-[55vh] flex items-center justify-center overflow-hidden pt-20">
                {/* background */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1618221639263-d0e336fabd16?w=1600&q=80"
                        alt=""
                        className="w-full h-full object-cover opacity-20"
                    />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #0e0e0e 0%, transparent 30%, #0e0e0e 100%)' }} />
                </div>

                <div className="relative z-10 text-center px-6 max-w-3xl">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="uppercase tracking-[0.3em] text-xs font-semibold mb-4"
                        style={{ color: '#D4AF37' }}
                    >
                        Get In Touch
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                        className="font-serif text-5xl md:text-7xl text-white mb-5 leading-tight"
                    >
                        Let's Create Something <em className="not-italic" style={{ color: '#D4AF37' }}>Remarkable</em>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                        className="text-gray-400 text-lg font-light"
                    >
                        Reach us instantly on WhatsApp — describe your project and we'll respond within the hour.
                    </motion.p>
                </div>
            </section>

            {/* ── Core content ── */}
            <section className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-5 gap-12">

                {/* ── Left: info + quick links ── */}
                <div className="lg:col-span-2 space-y-10">

                    {/* WhatsApp card */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        className="rounded-sm p-8 text-center flex flex-col items-center gap-4"
                        style={{ background: 'linear-gradient(135deg, #128C7E20, #25D36620)', border: '1px solid #25D36640' }}
                    >
                        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: '#25D366' }}>
                            <MessageCircle size={30} color="white" />
                        </div>
                        <h3 className="font-serif text-2xl text-white">WhatsApp Us Now</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">
                            The fastest way to reach us. Send a message and our team will respond within 60 minutes during business hours.
                        </p>
                        <a
                            href={`https://wa.me/${WA_NUMBER}`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white text-sm font-semibold w-full justify-center transition-all duration-300 hover:brightness-110"
                            style={{ background: '#25D366', boxShadow: '0 6px 24px rgba(37,211,102,0.35)' }}
                        >
                            <MessageCircle size={16} />
                            +971 58 173 5200
                        </a>
                    </motion.div>

                    {/* Contact details */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
                        className="space-y-5"
                    >
                        {[
                            { icon: <Phone size={18} />, label: 'Phone', value: '+971 58 173 5200', href: 'tel:+971581735200' },
                            { icon: <Mail size={18} />, label: 'Email', value: 'info@royalstandarduae.com', href: 'mailto:info@royalstandarduae.com' },
                            { icon: <MapPin size={18} />, label: 'Location', value: 'Dubai, United Arab Emirates', href: 'https://maps.google.com' },
                            { icon: <Clock size={18} />, label: 'Hours', value: 'Mon – Sat: 8:00 AM – 7:00 PM', href: null },
                        ].map((c, i) => (
                            <div key={i} className="flex items-start gap-4 group">
                                <div
                                    className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5"
                                    style={{ background: 'rgba(212,175,55,0.12)', color: '#D4AF37' }}
                                >
                                    {c.icon}
                                </div>
                                <div>
                                    <p className="text-gray-600 text-xs uppercase tracking-widest mb-0.5">{c.label}</p>
                                    {c.href ? (
                                        <a href={c.href} target="_blank" rel="noreferrer" className="text-white text-sm hover:text-yellow-400 transition-colors">
                                            {c.value}
                                        </a>
                                    ) : (
                                        <p className="text-white text-sm">{c.value}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </motion.div>

                    {/* Why WhatsApp */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
                        className="rounded-sm p-6 space-y-3"
                        style={{ background: '#161616', border: '1px solid rgba(255,255,255,0.06)' }}
                    >
                        <p className="text-yellow-500 uppercase text-xs tracking-widest font-semibold mb-4">Why WhatsApp?</p>
                        {[
                            'Instant response — no hold music',
                            'Share photos of your space directly',
                            'Get a quote faster than a phone call',
                            'Available in Arabic & English',
                        ].map((item, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <CheckCircle size={16} style={{ color: '#25D366' }} className="flex-shrink-0" />
                                <span className="text-gray-400 text-sm">{item}</span>
                            </div>
                        ))}
                    </motion.div>
                </div>

                {/* ── Right: smart enquiry builder ── */}
                <motion.div
                    initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                    className="lg:col-span-3"
                >
                    <div
                        className="rounded-sm p-8 md:p-10 h-full"
                        style={{ background: '#161616', border: '1px solid rgba(212,175,55,0.12)' }}
                    >
                        {/* header */}
                        <div className="mb-8">
                            <p className="uppercase tracking-widest text-xs font-semibold mb-2" style={{ color: '#D4AF37' }}>
                                Quick Message Builder
                            </p>
                            <h2 className="font-serif text-3xl text-white mb-2">Send via WhatsApp</h2>
                            <p className="text-gray-500 text-sm">
                                Fill in the details below — we'll open WhatsApp with your message pre-written and ready to send.
                            </p>
                        </div>

                        <div className="space-y-6">
                            {/* Name */}
                            <div>
                                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">Your Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Ahmed Al Mansoori"
                                    className="w-full bg-[#0e0e0e] border border-gray-700 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-yellow-500 transition-colors"
                                />
                            </div>

                            {/* Service */}
                            <div>
                                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">Service Required</label>
                                <select
                                    value={service}
                                    onChange={(e) => setService(e.target.value)}
                                    className="w-full bg-[#0e0e0e] border border-gray-700 px-4 py-3 text-sm text-white focus:outline-none focus:border-yellow-500 transition-colors appearance-none"
                                >
                                    <option value="">Select a service…</option>
                                    {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>

                            {/* Message */}
                            <div>
                                <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">Tell Us About Your Project</label>
                                <textarea
                                    rows={5}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Describe your space, requirements, timeline or any questions…"
                                    className="w-full bg-[#0e0e0e] border border-gray-700 px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-yellow-500 transition-colors resize-none"
                                />
                            </div>

                            {/* Preview */}
                            {(name || service || message) && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="rounded p-4 text-xs text-gray-400 leading-relaxed"
                                    style={{ background: 'rgba(37,211,102,0.06)', border: '1px solid rgba(37,211,102,0.15)' }}
                                >
                                    <p className="text-green-400 uppercase tracking-wider text-[10px] mb-2">Message Preview</p>
                                    <p className="whitespace-pre-line text-gray-300" style={{ fontSize: '12px' }}>
                                        {[
                                            `Hello Royal Standard! 👋`,
                                            name ? `My name is ${name}.` : '',
                                            service ? `I'm interested in: *${service}*.` : '',
                                            message ? `\n${message}` : '',
                                            '\nLooking forward to hearing from you.',
                                        ].filter(Boolean).join('\n')}
                                    </p>
                                </motion.div>
                            )}

                            {/* CTA */}
                            <a
                                href={buildWaLink(name, service, message)}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-center gap-3 w-full py-4 text-white text-sm uppercase tracking-widest font-semibold transition-all duration-300 hover:brightness-110 rounded-full"
                                style={{ background: '#25D366', boxShadow: '0 6px 32px rgba(37,211,102,0.3)' }}
                            >
                                <MessageCircle size={18} />
                                WHATSAPP US
                                <ArrowRight size={16} />
                            </a>

                            <p className="text-gray-700 text-xs text-center">
                                This will open WhatsApp with your message pre-filled. No account needed on our end.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* ── Map / location strip ── */}
            <section className="border-t border-white/5">
                <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                    {[
                        { icon: <MapPin size={22} />, title: 'Serving All UAE', sub: 'Dubai · Abu Dhabi · Sharjah · Ajman' },
                        { icon: <Clock size={22} />, title: 'Business Hours', sub: 'Mon – Sat  |  8:00 AM – 7:00 PM' },
                        { icon: <MessageCircle size={22} />, title: 'WhatsApp Response', sub: 'Usually within 60 minutes' },
                    ].map((c, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                            className="flex flex-col items-center gap-3"
                        >
                            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.1)', color: '#D4AF37' }}>
                                {c.icon}
                            </div>
                            <h4 className="text-white font-serif text-lg">{c.title}</h4>
                            <p className="text-gray-500 text-sm">{c.sub}</p>
                        </motion.div>
                    ))}
                </div>
            </section>




            {/* floating WA handled globally in App.js */}
        </div>
    );
};

export default ContactPage;
